import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { usePaginateQuery } from "@/hooks";
import { adminApi } from "@/lib/api";
import { type Paginate, type SearchRequest } from "@/types";

import {
  type ReportProvider,
  type ReportSource,
  type ReportSourceMetadata,
} from "@/modules/report/shared/type";

import {
  type AdminReport,
  type AdminReportColumn,
  type AdminReportRelation,
} from "../types";

export type ReportList = Paginate<AdminReport>;

export const reports = createQueryKeys("admin-report", {
  all: (page?: number, filter?: SearchRequest) => [{ page, filter }],
  one: (id?: number) => [id],
  providers: null,
  sources: (provider?: string) => ({
    queryKey: [provider],
    contextQueries: {
      metadata: (source?: string) => [source],
    },
  }),
});

const include = "columns,relations";

export const fetchReportsQueryOptions = (
  page: number = 0,
  search?: SearchRequest,
) =>
  queryOptions({
    ...reports.all(page, search),
    queryFn: ({ signal }) =>
      adminApi
        .get<ReportList>("report/reports", {
          params: { page, ...search },
          signal,
        })
        .then((response) => response.data),
  });

export const useFetchReports = (search?: SearchRequest) =>
  usePaginateQuery((page) => fetchReportsQueryOptions(page, search));

export const fetchReportQueryOptions = (id?: number) =>
  queryOptions({
    ...reports.one(id),
    queryFn: () =>
      adminApi
        .get<AdminReport<true, true>>(`report/reports/${id}`, {
          params: { include },
        })
        .then((response) => response.data),
    enabled: id !== undefined,
  });

export const useFetchReport = (id?: number) =>
  useQuery(fetchReportQueryOptions(id));

export interface ReportRequest {
  name: string;
  provider?: string;
  source?: string;
  columns: AdminReportColumn[];
  relations: AdminReportRelation[];
}

export const useCreateReport = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ReportRequest) =>
      adminApi
        .post<
          AdminReport<true, true>
        >("report/reports", data, { params: { include } })
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: reports.all._def,
      });
    },
  });
};

export const useUpdateReport = (id: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ReportRequest) =>
      adminApi
        .put<
          AdminReport<true, true>
        >(`report/reports/${id}`, data, { params: { include } })
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: reports.all._def,
      });
      queryClient.invalidateQueries(reports.one(id));
    },
  });
};

export const useCreateOrUpdateReport = (id: number | undefined) => {
  const createReport = useCreateReport();
  const updateReport = useUpdateReport(id!);
  return id ? updateReport : createReport;
};

export const useDeleteReport = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => adminApi.delete(`report/reports/${id}`),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({
        queryKey: reports.all._def,
      });
      queryClient.invalidateQueries(reports.one(id));
    },
  });
};

export const usePublishReport = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => adminApi.put(`report/reports/${id}/publish`),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({
        queryKey: reports.all._def,
      });
      queryClient.invalidateQueries(reports.one(id));
    },
  });
};

export const fetchReportProvidersQueryOptions = () =>
  queryOptions({
    ...reports.providers,
    queryFn: () =>
      adminApi
        .get<ReportProvider[]>(`report/providers`)
        .then((response) => response.data),
  });

export const useFetchReportProviders = () =>
  useQuery(fetchReportProvidersQueryOptions());

export const fetchReportProviderSourcesQueryOptions = (
  provider: string | undefined,
) =>
  queryOptions({
    ...reports.sources(provider),
    queryFn: () =>
      adminApi
        .get<ReportSource[]>(`report/providers/${provider}/sources`)
        .then((response) => response.data),
    enabled: provider !== undefined,
  });

export const useFetchReportProviderSources = (provider: string | undefined) =>
  useQuery(fetchReportProviderSourcesQueryOptions(provider));

export const fetchReportSourceMetadataQueryOptions = (
  provider: string | undefined,
  source: string | undefined,
) =>
  queryOptions({
    ...reports.sources(provider)._ctx.metadata(source),
    queryFn: () =>
      adminApi
        .get<ReportSourceMetadata>(
          `report/providers/${provider}/sources/${source}/metadata`,
        )
        .then((response) => response.data),
    enabled: provider !== undefined && source !== undefined,
  });

export const useFetchReportSourceMetadata = (
  provider: string | undefined,
  source: string | undefined,
) => useQuery(fetchReportSourceMetadataQueryOptions(provider, source));
